import { env } from '@core/config/env';
import { getStoredToken, handleApiResponse } from '@core/index';
import type { User, CreateUserRequest, UpdateUserRequest, Role, CreateRoleRequest, UpdateRoleRequest } from '../types';
import type { GameInfo } from '@modules/auth/services/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'ngrok-skip-browser-warning': '69420',
  Authorization: `Bearer ${getStoredToken()}`,
});

// User API
export const getUsers = async (): Promise<User[]> => {
  const response = await fetch(`${env.apiBaseUrl}/users`, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleApiResponse<User[]>(response, 'Failed to fetch users');
};

export const createUser = async (data: CreateUserRequest): Promise<User> => {
  const response = await fetch(`${env.apiBaseUrl}/users`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return handleApiResponse<User>(response, 'Failed to create user');
};

export const updateUser = async (userId: number, data: UpdateUserRequest): Promise<User> => {
  const response = await fetch(`${env.apiBaseUrl}/users/${userId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return handleApiResponse<User>(response, 'Failed to update user');
};

// Role API
export const getRoles = async (): Promise<Role[]> => {
  const response = await fetch(`${env.apiBaseUrl}/users/roles`, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleApiResponse<Role[]>(response, 'Failed to fetch roles');
};

export const createRole = async (data: CreateRoleRequest): Promise<Role> => {
  const response = await fetch(`${env.apiBaseUrl}/users/role`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return handleApiResponse<Role>(response, 'Failed to create role');
};

export const updateRole = async (roleId: number, data: UpdateRoleRequest): Promise<Role> => {
  const response = await fetch(`${env.apiBaseUrl}/users/role/${roleId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return handleApiResponse<Role>(response, 'Failed to update role');
};

// Delete Role API
export const deleteRole = async (roleId: number): Promise<void> => {
  const response = await fetch(`${env.apiBaseUrl}/users/role/${roleId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  // We don't need the response body for deletes
  await handleApiResponse(response, 'Failed to delete role');
};

// Games API
export const getGames = async (): Promise<GameInfo[]> => {
  const response = await fetch(`${env.apiBaseUrl}/games`, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleApiResponse<GameInfo[]>(response, 'Failed to fetch games');
};
